import React from "react";
import type { SmCostTrendItem } from "../type";
import { colors } from "../../../utils/colors";
import { useApp } from "../../../contexts/AppContext";
import { GenericBarChart, GenericBarChartRow } from "./GenericBarChart";
import {
  EMPTY_SM_COST_STATE_MESSAGE,
  SM_COST_SERIES_NAME,
} from "../../../utils/consts";

const monthShort = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const toChartRows = (series: SmCostTrendItem[]) =>
  series.map((p) => ({
    label: `${monthShort[p.month - 1] ?? p.month} ${p.year}`,
    value: Number(p.margin_pct),
  }));

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

export interface SmCostTrendChartProps {
  title?: string;
  loading?: boolean;
  series: SmCostTrendItem[];
}

export const SmCostTrendChart: React.FC<SmCostTrendChartProps> = ({
  title,
  loading = false,
  series,
}) => {
  const { drawerOpen } = useApp();

  const rows: GenericBarChartRow[] = toChartRows(series);

  return (
    <GenericBarChart
      title={title}
      data={rows}
      seriesName={SM_COST_SERIES_NAME}
      loading={loading}
      height={400}
      minHeight={400}
      emptyMessage={EMPTY_SM_COST_STATE_MESSAGE}
      legendTop={8}
      legendRight={50}
      barWidth="35%"
      xAxisLabelColor={colors.black}
      xAxisLabelFontSize="0.625rem"
      xAxisLabelFontWeight={500}
      yAxisLabelColor={colors.chart_gray}
      yAxisLabelFontSize="0.6875rem"
      xAxisRotateThreshold={5}
      defaultBarColor={colors.bar_gray}
      yAxisValueFormatter={(val: number) => `${Number(val).toFixed(1)}%`}
      axisTooltipFormatter={({
        dataIndex,
        categoryLabel,
        primaryValue,
        seriesMarkerHtml,
      }) => {
        const point = series[dataIndex];
        const pct = `${primaryValue.toFixed(2)}%`;
        const cost =
          point === undefined
            ? "N/A"
            : formatCurrency(point.sm_margin_value);
        return `<div style="color:${colors.chart_text};">${categoryLabel}<br/><span style="display:inline-block;margin-top:6px;">${seriesMarkerHtml}${SM_COST_SERIES_NAME} <span style="display:inline-block;padding-left:14px;"><strong>${pct}</strong> (<strong>${cost}</strong>)</span></span></div>`;
      }}
      resizeTrigger={drawerOpen}
    />
  );
};
